import { Box, CircularProgress, Divider, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { Typography, SizingMultiplier } from '@ocx/ui';
import { PaymentInstrumentType } from '@ocx/graphql';
import { PaymentInstrument } from '@ocx/data-payment-instruments';
import { ListSelector } from '@ocx-app/hooks/useListController';
import { IStoreCarWashOption } from '@ocx-app/modules/stores/types';
import { ModalPaper } from '@ocx-app/components/modals';

import { SelectedPumpComponent } from './selected-pump.component';
import { PrintReceipt } from './print-receipt.component';
import { PaymentMethodSelector } from './payment-method-selector.component';
import { PayButton } from './pay-button.component';
import { CarWashSelector } from './car-wash-selector.component';

type PaymentSectionProps = {
  selectedPump: string | null;
  shouldPrintReceipt: boolean;
  togglePrintReceipt(value?: boolean): void;
  handlePurchaseGooglePay(): void;
  handlePurchaseApplePay(): void;
  onAddPaymentMethodClick(): void;
  handlePurchaseCard(): void;
  deselectPump(): void;
  carWashListController: ListSelector<IStoreCarWashOption>;
  paymentInstrumentsListSelector: ListSelector<PaymentInstrument>;
  isPaymentInstrumentsLoading: boolean;
  isCardsAvailable: boolean;
};

export const PaymentSection = (props: PaymentSectionProps) => {
  const {
    selectedPump,
    shouldPrintReceipt,
    togglePrintReceipt,
    deselectPump,
    handlePurchaseGooglePay,
    handlePurchaseApplePay,
    onAddPaymentMethodClick,
    handlePurchaseCard,
    carWashListController,
    paymentInstrumentsListSelector,
    isPaymentInstrumentsLoading,
    isCardsAvailable,
  } = props;

  return (
    <ModalPaper sx={{ minHeight: 400 }}>
      <Stack
        direction="row"
        justifyContent="center"
        spacing={SizingMultiplier.md}
        p={[SizingMultiplier.lg, SizingMultiplier.md]}>
        {selectedPump !== null && <SelectedPumpComponent pumpNumber={selectedPump} onClick={deselectPump} />}
        {carWashListController.listItems.length > 0 && (
          <CarWashSelector carWashListController={carWashListController} />
        )}
      </Stack>
      <Divider />
      <Box marginTop={SizingMultiplier.xl} marginBottom={SizingMultiplier.lg}>
        <Typography variant="h6" align="center" color="textSecondary">
          <FormattedMessage
            id="paymentSection:payingWithText"
            defaultMessage="Paying With"
            description="Pay at pump: 'paying with' section title text"
          />
        </Typography>
      </Box>
      {isPaymentInstrumentsLoading ? (
        <Box my={SizingMultiplier.lg} alignSelf="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isCardsAvailable && (
            <Box px={SizingMultiplier.md} paddingBottom={SizingMultiplier.sm}>
              <PaymentMethodSelector
                paymentInstrumentsListSelector={paymentInstrumentsListSelector}
                onAddPaymentMethodClick={onAddPaymentMethodClick}
              />
            </Box>
          )}
          <Box px={SizingMultiplier.md} paddingBottom={SizingMultiplier.md}>
            <Typography variant="caption" color="textSecondary" component="p">
              <FormattedMessage
                id="payAtPump:preAuthMoneyInfoMessage"
                defaultMessage="You may see a temporary hold. You will only be charged for your actual purchase amount."
                description="Pay At Pump: pre-authorisation money info message"
              />
            </Typography>
            {paymentInstrumentsListSelector.selectedListItem?.paymentType === PaymentInstrumentType.Prepaid && (
              <Typography variant="caption" color="textSecondary" component="p" mt={SizingMultiplier.sm}>
                <FormattedMessage
                  id="payAtPump:prepaid-payment-instrument:info-message"
                  defaultMessage="The fuel pump will only authorize up to current available gift card amount shown."
                  description="Pay At Pump: info message for prepaid payment instrument"
                />
              </Typography>
            )}
          </Box>
          <Box px={SizingMultiplier.md} paddingBottom={SizingMultiplier.md}>
            <PrintReceipt
              checked={Boolean(shouldPrintReceipt || carWashListController.selectedListItem)}
              disabled={Boolean(carWashListController.selectedListItem)}
              onClick={togglePrintReceipt}
            />
          </Box>
          <Box px={SizingMultiplier.md} paddingBottom={SizingMultiplier.lg}>
            <PayButton
              onGooglePayButtonClick={handlePurchaseGooglePay}
              onApplePayButtonClick={handlePurchaseApplePay}
              onCardPayButtonClick={handlePurchaseCard}
              paymentInstruments={paymentInstrumentsListSelector.listItems}
              selectedPaymentInstrument={paymentInstrumentsListSelector.selectedListItem}
              isListMode={!isCardsAvailable}
            />
          </Box>
        </>
      )}
    </ModalPaper>
  );
};
